import { useMemo, useState, type ComponentType } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router';
import { userSetupStepKeys, type UserSetupStepKey } from '@brimo/shared';
import { Banner, Button } from '../../../ds/components';
import { errorMessage } from '../../../lib/api';
import { ShellSkeleton } from '../../../shell/ShellSkeleton';
import { useStepAction, useUserSetup } from './api';
import { WizardContext, type WizardContextValue } from './StepFrame';
import { AvailabilityStep } from './steps/AvailabilityStep';
import { BillingStep } from './steps/BillingStep';
import { CalendarStep } from './steps/CalendarStep';
import { MailboxStep } from './steps/MailboxStep';
import { ProfileStep } from './steps/ProfileStep';
import { SecurityStep } from './steps/SecurityStep';
import { SignatureStep } from './steps/SignatureStep';
import { WhatsAppStep } from './steps/WhatsAppStep';
import styles from './UserWizard.module.css';

export const USER_WIZARD_PATH = '/setup/user';

function isStepKey(value: string | undefined): value is UserSetupStepKey {
  return value !== undefined && (userSetupStepKeys as readonly string[]).includes(value);
}

export function stepPath(key: UserSetupStepKey): string {
  return `${USER_WIZARD_PATH}/${key}`;
}

const STEP_COMPONENTS: Record<UserSetupStepKey, ComponentType> = {
  profile: ProfileStep,
  mailbox: MailboxStep,
  calendar: CalendarStep,
  availability: AvailabilityStep,
  whatsapp: WhatsAppStep,
  signature: SignatureStep,
  billing: BillingStep,
  security: SecurityStep,
};

/**
 * `/setup/user/:step` (Onboarding Nutzer 02–11, M2–M9) below `RequireSetup`: loads `GET /setup/user`, resumes at
 * `current` when no step is named, renders the step with the wizard's navigation.
 */
export function UserWizard() {
  const params = useParams();
  const navigate = useNavigate();
  const setup = useUserSetup();
  const action = useStepAction();
  const [acting, setActing] = useState<'continue' | 'skip' | null>(null);
  const [actError, setActError] = useState<{ step: string; message: string } | null>(null);
  const step = isStepKey(params['step']) ? params['step'] : null;

  const value = useMemo<WizardContextValue | null>(() => {
    if (!setup.data || step === null) return null;
    const index = userSetupStepKeys.indexOf(step);
    const next = userSetupStepKeys[index + 1];
    const previous = index > 0 ? userSetupStepKeys[index - 1] : undefined;
    const goNext = () => {
      setActError(null);
      void navigate(next ? stepPath(next) : `${USER_WIZARD_PATH}/done`);
    };
    return {
      setup: setup.data,
      step,
      goNext,
      goBack: previous
        ? () => {
            setActError(null);
            void navigate(stepPath(previous));
          }
        : null,
      act: async (kind) => {
        setActing(kind);
        setActError(null);
        try {
          await action.mutateAsync({ key: step, action: kind });
          goNext();
          return true;
        } catch (error) {
          setActError({ step, message: errorMessage(error) });
          return false;
        } finally {
          setActing(null);
        }
      },
      acting,
      actError: actError && actError.step === step ? actError.message : null,
    };
  }, [setup.data, step, navigate, action, acting, actError]);

  if (setup.isError) {
    return (
      <div className={styles.error}>
        <Banner
          tone="danger"
          actions={
            <Button variant="secondary" size="small" onClick={() => void setup.refetch()}>
              Erneut laden
            </Button>
          }
        >
          {errorMessage(setup.error)}
        </Banner>
      </div>
    );
  }
  if (!setup.data) return <ShellSkeleton />;
  if (step === null) {
    const current = setup.data.current;
    return <Navigate to={current ? stepPath(current) : `${USER_WIZARD_PATH}/done`} replace />;
  }
  if (value === null) return <ShellSkeleton />;
  const Step = STEP_COMPONENTS[step];
  return (
    <WizardContext.Provider value={value}>
      <Step key={step} />
    </WizardContext.Provider>
  );
}
